import { useCallback, useEffect, useMemo, useState } from 'react';
import { ProCard } from '@ant-design/pro-components';
import AdminProTable from '@/components/admin-pro-table';
import { App, Button, Flex, Form, Input, InputNumber, Modal, Space, Tag, Typography } from 'antd';
import { useSetState } from 'ahooks';
import Permission, { useAccess } from '@/components/permission';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';

import {
    resolveUserShareMetrics,
    userShareActivityFinalize,
    userShareAttemptApprove,
    userShareAttemptList,
    userShareAttemptReject,
    userShareAttemptUpdateMetrics,
    type UserShareAttempt,
    type UserShareAttemptStatus,
} from '@/services/user-share';
import { useActivity1011Config } from '@/pages/activity-1011/task-config/hooks/useActivity1011Config';
import { resolveApiResult } from '@/utils/apiResponse';

import type { ProColumns } from '@ant-design/pro-components';
import type { PaginationProps } from 'antd';

const PERMISSION = 'Dramas_Activity-1011_Share-Review';
const REVIEW_PERMISSION = 'Dramas_Activity-1011_Share-Review_Review';

/** 1011 活动 ID；可用 VITE_ACTIVITY_1011_ID 覆盖 */
const DEFAULT_ACTIVITY_ID = String(import.meta.env.VITE_ACTIVITY_1011_ID || '1011');

const DEFAULT_PAGINATION: PaginationProps = {
    current: 1,
    pageSize: 20,
};

type StatusTab = 'all' | UserShareAttemptStatus;

const normalizeStatus = (status?: string | null): UserShareAttemptStatus => {
    const raw = String(status ?? '')
        .trim()
        .toLowerCase();
    if (['unsubmitted', '未提交'].includes(raw)) return 'unsubmitted';
    if (!raw || ['pending', 'pending_approval', '0', 'init', 'waiting', '待审核'].includes(raw)) {
        return 'pending_approval';
    }
    if (['approved', '1', 'passed', 'success', '已通过'].includes(raw)) return 'approved';
    if (['rejected', '2', 'refuse', 'failed', '已拒绝'].includes(raw)) return 'rejected';
    return 'pending_approval';
};

const getAttemptId = (row: UserShareAttempt) => String(row.id || row.attempt_id || '');

const getUid = (row: UserShareAttempt) => row.user_id || row.uid || '-';

const getLink = (row: UserShareAttempt) => row.link || row.url || row.share_url || '';

const getCode = (row: UserShareAttempt) => row.verify_code || row.captcha || row.code || '-';

const getRejectReason = (row: UserShareAttempt) =>
    row.rejection_reason || row.reject_reason || row.reason || '-';

const AUTO_APPROVE_HOURS = 48;

/** 毫秒/秒级时间戳（字符串需先转数字，避免 dayjs 按日期字符串误解析） */
const parseTimestamp = (value?: string | number | null) => {
    if (value === undefined || value === null || value === '') return null;
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    const ms = String(Math.trunc(num)).length === 10 ? num * 1000 : num;
    const d = dayjs(ms);
    return d.isValid() ? d : null;
};

const formatTime = (value?: string | number | null) => {
    const d = parseTimestamp(value);
    return d ? d.format('YYYY-MM-DD HH:mm:ss') : '-';
};

const getAutoApproveAt = (row: UserShareAttempt) => {
    const explicit = row.auto_approve_deadline || row.auto_approve_at || row.auto_pass_at;
    if (explicit) return formatTime(explicit);
    const submitted = parseTimestamp(row.submitted_at || row.created_at);
    if (!submitted) return '-';
    return submitted.add(AUTO_APPROVE_HOURS, 'hour').format('YYYY-MM-DD HH:mm:ss');
};

const PageShareReview: React.FC = () => {
    const { message, modal } = App.useApp();
    const { formatMessage } = useIntl();
    const access = useAccess();
    const canReview = access(REVIEW_PERMISSION);

    const [pagination, setPagination] = useSetState<PaginationProps>({ ...DEFAULT_PAGINATION });
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [dataSource, setDataSource] = useState<UserShareAttempt[]>([]);
    const [statusTab, setStatusTab] = useState<StatusTab>('pending_approval');
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectTarget, setRejectTarget] = useState<UserShareAttempt | null>(null);
    const [rejectForm] = Form.useForm<{ reason: string }>();
    const [metricsOpen, setMetricsOpen] = useState(false);
    const [metricsTarget, setMetricsTarget] = useState<UserShareAttempt | null>(null);
    const [metricsForm] = Form.useForm<{ plays?: number; likes?: number; shares?: number }>();

    const { config: activityConfig, loadConfig } = useActivity1011Config();

    useEffect(() => {
        loadConfig().catch(() => undefined);
    }, [loadConfig]);

    /** 修改指标仅在活动结束后开放；拿不到结束时间时保持关闭 */
    const activityEnded = useMemo(() => {
        const endAt = activityConfig.activityEndAt;
        if (!endAt) return false;
        return dayjs().isAfter(dayjs(endAt));
    }, [activityConfig.activityEndAt]);

    const activityTimeText = useMemo(() => {
        const start = formatTime(activityConfig.activityStartAt);
        const end = formatTime(activityConfig.activityEndAt);
        if (start === '-' && end === '-') return null;
        return `${start} ~ ${end}`;
    }, [activityConfig.activityEndAt, activityConfig.activityStartAt]);

    const activityId = useMemo(() => {
        const fromRow = dataSource.find((row) => row.activity_id)?.activity_id;
        return String(fromRow || DEFAULT_ACTIVITY_ID);
    }, [dataSource]);

    const fetchList = useCallback(
        async (page = 1, pageSize = 20, tab: StatusTab = 'pending_approval') => {
            setLoading(true);
            try {
                const response = await userShareAttemptList({
                    page,
                    size: pageSize,
                    ...(tab !== 'all' ? { status: tab } : {}),
                });
                const { data } = response;
                if (data.code !== 200) {
                    message.error(data.msg);
                    return;
                }

                const rows = Array.isArray(data.data) ? (data.data as UserShareAttempt[]) : [];
                setDataSource(rows);

                const meta = data.meta as { page?: number; size?: number; total?: number } | null | undefined;
                if (meta) {
                    setPagination({
                        current: Number(meta.page) > 0 ? Number(meta.page) : page,
                        pageSize: Number(meta.size) > 0 ? Number(meta.size) : pageSize,
                        total: meta.total ?? rows.length,
                    });
                } else {
                    setPagination({
                        current: page,
                        pageSize,
                        total: rows.length,
                    });
                }
            } catch (error: any) {
                message.error(error?.response?.data?.msg || error?.message || 'Failed to fetch list');
            } finally {
                setLoading(false);
            }
        },
        [message, setPagination],
    );

    useEffect(() => {
        void fetchList(1, DEFAULT_PAGINATION.pageSize as number, statusTab);
    }, [fetchList, statusTab]);

    const handleApprove = useCallback(
        (record: UserShareAttempt) => {
            const attemptId = getAttemptId(record);
            if (!attemptId) {
                message.error(formatMessage({ id: 'shareReview.errors.missingId' }));
                return;
            }
            modal.confirm({
                title: formatMessage({ id: 'shareReview.actions.approveConfirm' }),
                onOk: async () => {
                    setSubmitting(true);
                    try {
                        const res = await userShareAttemptApprove(attemptId);
                        const result = resolveApiResult(res.data);
                        if (!result.success) {
                            message.error(result.msg);
                            return;
                        }
                        message.success(result.msg || formatMessage({ id: 'shareReview.toast.approved' }));
                        await fetchList(pagination.current as number, pagination.pageSize as number, statusTab);
                    } catch (error: any) {
                        message.error(error?.response?.data?.msg || error?.message);
                    } finally {
                        setSubmitting(false);
                    }
                },
            });
        },
        [fetchList, formatMessage, message, modal, pagination.current, pagination.pageSize, statusTab],
    );

    const openReject = useCallback(
        (record: UserShareAttempt) => {
            setRejectTarget(record);
            rejectForm.resetFields();
            setRejectOpen(true);
        },
        [rejectForm],
    );

    const submitReject = useCallback(async () => {
        const attemptId = rejectTarget ? getAttemptId(rejectTarget) : '';
        if (!attemptId) {
            message.error(formatMessage({ id: 'shareReview.errors.missingId' }));
            return;
        }
        try {
            const values = await rejectForm.validateFields();
            setSubmitting(true);
            const res = await userShareAttemptReject(attemptId, { reason: values.reason.trim() });
            const result = resolveApiResult(res.data);
            if (!result.success) {
                message.error(result.msg);
                return;
            }
            message.success(result.msg || formatMessage({ id: 'shareReview.toast.rejected' }));
            setRejectOpen(false);
            setRejectTarget(null);
            await fetchList(pagination.current as number, pagination.pageSize as number, statusTab);
        } catch (error: any) {
            if (error?.errorFields) return;
            message.error(error?.response?.data?.msg || error?.message);
        } finally {
            setSubmitting(false);
        }
    }, [
        fetchList,
        formatMessage,
        message,
        pagination.current,
        pagination.pageSize,
        rejectForm,
        rejectTarget,
        statusTab,
    ]);

    const openMetrics = useCallback(
        (record: UserShareAttempt) => {
            setMetricsTarget(record);
            const metrics = resolveUserShareMetrics(record);
            metricsForm.setFieldsValue({
                plays: metrics.plays,
                likes: metrics.likes,
                shares: metrics.shares,
            });
            setMetricsOpen(true);
        },
        [metricsForm],
    );

    const submitMetrics = useCallback(async () => {
        const attemptId = metricsTarget ? getAttemptId(metricsTarget) : '';
        if (!attemptId) {
            message.error(formatMessage({ id: 'shareReview.errors.missingId' }));
            return;
        }
        try {
            const values = await metricsForm.validateFields();
            setSubmitting(true);
            const res = await userShareAttemptUpdateMetrics(attemptId, {
                plays: values.plays,
                likes: values.likes,
                shares: values.shares,
            });
            const result = resolveApiResult(res.data);
            if (!result.success) {
                message.error(result.msg);
                return;
            }
            message.success(result.msg || formatMessage({ id: 'shareReview.toast.metricsUpdated' }));
            setMetricsOpen(false);
            setMetricsTarget(null);
            await fetchList(pagination.current as number, pagination.pageSize as number, statusTab);
        } catch (error: any) {
            if (error?.errorFields) return;
            message.error(error?.response?.data?.msg || error?.message);
        } finally {
            setSubmitting(false);
        }
    }, [
        fetchList,
        formatMessage,
        message,
        metricsForm,
        metricsTarget,
        pagination.current,
        pagination.pageSize,
        statusTab,
    ]);

    const handleFinalize = useCallback(() => {
        if (!activityId) {
            message.error(formatMessage({ id: 'shareReview.errors.missingActivityId' }));
            return;
        }
        if (!activityEnded) {
            message.warning(formatMessage({ id: 'shareReview.finalize.disabledHint' }));
            return;
        }
        modal.confirm({
            title: formatMessage({ id: 'shareReview.finalize.title' }),
            content: formatMessage({ id: 'shareReview.finalize.content' }),
            okText: formatMessage({ id: 'shareReview.finalize.ok' }),
            cancelText: formatMessage({ id: 'b.cancel' }),
            onOk: async () => {
                setSubmitting(true);
                try {
                    const res = await userShareActivityFinalize(activityId, {});
                    const result = resolveApiResult(res.data);
                    if (!result.success) {
                        message.error(result.msg);
                        return;
                    }
                    message.success(result.msg || formatMessage({ id: 'shareReview.toast.finalized' }));
                } catch (error: any) {
                    message.error(error?.response?.data?.msg || error?.message);
                } finally {
                    setSubmitting(false);
                }
            },
        });
    }, [activityEnded, activityId, formatMessage, message, modal]);

    const statusTag = useCallback((status?: string) => {
        const normalized = normalizeStatus(status);
        if (normalized === 'approved') {
            return (
                <Tag color="success">
                    <FormattedMessage id="shareReview.status.approved" />
                </Tag>
            );
        }
        if (normalized === 'rejected') {
            return (
                <Tag color="error">
                    <FormattedMessage id="shareReview.status.rejected" />
                </Tag>
            );
        }
        if (normalized === 'unsubmitted') {
            return (
                <Tag>
                    <FormattedMessage id="shareReview.status.unsubmitted" />
                </Tag>
            );
        }
        return (
            <Tag color="processing">
                <FormattedMessage id="shareReview.status.pending" />
            </Tag>
        );
    }, []);

    const columns = useMemo<ProColumns<UserShareAttempt>[]>(
        () => [
            {
                title: <FormattedMessage id="shareReview.columns.uid" />,
                width: 160,
                render: (_, record) => {
                    const uid = getUid(record);
                    return uid === '-' ? (
                        '-'
                    ) : (
                        <Typography.Text copyable={{ text: uid }}>{uid}</Typography.Text>
                    );
                },
            },
            {
                title: <FormattedMessage id="shareReview.columns.platform" />,
                dataIndex: 'platform',
                width: 100,
                render: (_, record) =>
                    record.platform ? String(record.platform).toUpperCase() : '-',
            },
            {
                title: <FormattedMessage id="shareReview.columns.link" />,
                width: 220,
                ellipsis: true,
                render: (_, record) => {
                    const link = getLink(record);
                    if (!link) return '-';
                    return (
                        <Typography.Link href={link} target="_blank" rel="noreferrer">
                            {link}
                        </Typography.Link>
                    );
                },
            },
            {
                title: <FormattedMessage id="shareReview.columns.code" />,
                width: 120,
                render: (_, record) => getCode(record),
            },
            {
                title: <FormattedMessage id="shareReview.columns.plays" />,
                width: 90,
                align: 'right',
                render: (_, record) => resolveUserShareMetrics(record).plays,
            },
            {
                title: <FormattedMessage id="shareReview.columns.likes" />,
                width: 90,
                align: 'right',
                render: (_, record) => resolveUserShareMetrics(record).likes,
            },
            {
                title: <FormattedMessage id="shareReview.columns.shares" />,
                width: 90,
                align: 'right',
                render: (_, record) => resolveUserShareMetrics(record).shares,
            },
            {
                title: <FormattedMessage id="shareReview.columns.submittedAt" />,
                width: 180,
                render: (_, record) => formatTime(record.submitted_at || record.created_at),
            },
            {
                title: <FormattedMessage id="shareReview.columns.autoApproveAt" />,
                width: 180,
                render: (_, record) => getAutoApproveAt(record),
            },
            {
                title: <FormattedMessage id="shareReview.columns.status" />,
                width: 100,
                render: (_, record) => statusTag(record.status as string),
            },
            {
                title: <FormattedMessage id="shareReview.columns.rejectReason" />,
                width: 160,
                ellipsis: true,
                render: (_, record) =>
                    normalizeStatus(record.status as string) === 'rejected' ? getRejectReason(record) : '-',
            },
            {
                title: <FormattedMessage id="shareReview.columns.action" />,
                width: 220,
                fixed: 'right',
                render: (_, record) => {
                    const status = normalizeStatus(record.status as string);
                    const canEditMetrics = canReview && status === 'approved' && activityEnded;
                    return (
                        <Space wrap>
                            {canReview && status === 'pending_approval' ? (
                                <>
                                    <Button
                                        type="link"
                                        size="small"
                                        loading={submitting}
                                        onClick={() => handleApprove(record)}
                                    >
                                        <FormattedMessage id="shareReview.actions.approve" />
                                    </Button>
                                    <Button
                                        type="link"
                                        size="small"
                                        danger
                                        loading={submitting}
                                        onClick={() => openReject(record)}
                                    >
                                        <FormattedMessage id="shareReview.actions.reject" />
                                    </Button>
                                </>
                            ) : null}
                            {canEditMetrics ? (
                                <Button type="link" size="small" onClick={() => openMetrics(record)}>
                                    <FormattedMessage id="shareReview.actions.metrics" />
                                </Button>
                            ) : null}
                            {!canReview || (status !== 'pending_approval' && !canEditMetrics) ? '-' : null}
                        </Space>
                    );
                },
            },
        ],
        [activityEnded, canReview, handleApprove, openMetrics, openReject, statusTag, submitting],
    );

    const tabItems: { key: StatusTab; id: string }[] = [
        { key: 'all', id: 'shareReview.tabs.all' },
        { key: 'pending_approval', id: 'shareReview.tabs.pending' },
        { key: 'unsubmitted', id: 'shareReview.tabs.unsubmitted' },
        { key: 'approved', id: 'shareReview.tabs.approved' },
        { key: 'rejected', id: 'shareReview.tabs.rejected' },
    ];

    return (
        <Permission permission={PERMISSION}>
            <ProCard
                title={
                    <Space size="middle" wrap>
                        <FormattedMessage id="shareReview.title" />
                        {activityTimeText ? (
                            <Typography.Text type="secondary">
                                <FormattedMessage id="shareReview.activityTime" />
                                {': '}
                                {activityTimeText}
                            </Typography.Text>
                        ) : null}
                    </Space>
                }
                extra={
                    canReview ? (
                        <Button
                            danger
                            loading={submitting}
                            disabled={!activityEnded}
                            title={
                                activityEnded
                                    ? undefined
                                    : formatMessage({ id: 'shareReview.finalize.disabledHint' })
                            }
                            onClick={handleFinalize}
                        >
                            <FormattedMessage id="shareReview.finalize.button" />
                        </Button>
                    ) : null
                }
            >
                <Flex gap={8} wrap style={{ marginBottom: 16 }}>
                    {tabItems.map((tab) => (
                        <Button
                            key={tab.key}
                            type={statusTab === tab.key ? 'primary' : 'default'}
                            onClick={() => {
                                setPagination((prev) => ({ ...prev, current: 1 }));
                                setStatusTab(tab.key);
                            }}
                        >
                            <FormattedMessage id={tab.id} />
                        </Button>
                    ))}
                </Flex>

                <AdminProTable<UserShareAttempt>
                    bordered
                    columns={columns}
                    dataSource={dataSource}
                    loading={loading}
                    rowKey={(record) => getAttemptId(record) || JSON.stringify(record)}
                    search={false}
                    toolBarRender={() => [
                        <Typography.Text key="autoApproveHint" type="secondary">
                            <FormattedMessage id="shareReview.autoApproveHint" />
                        </Typography.Text>,
                    ]}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        onChange: (page, pageSize) => {
                            setPagination({ current: page, pageSize: pageSize ?? 20 });
                            fetchList(page, pageSize ?? 20, statusTab);
                        },
                    }}
                    options={{
                        reload: () => fetchList(pagination.current as number, pagination.pageSize as number, statusTab),
                        density: true,
                        setting: true,
                    }}
                    scroll={{ x: 1760 }}
                    locale={{
                        emptyText: formatMessage({ id: 'shareReview.empty' }),
                    }}
                />
            </ProCard>

            <Modal
                open={rejectOpen}
                title={<FormattedMessage id="shareReview.rejectModal.title" />}
                okText={<FormattedMessage id="shareReview.rejectModal.ok" />}
                cancelText={<FormattedMessage id="b.cancel" />}
                confirmLoading={submitting}
                onCancel={() => {
                    setRejectOpen(false);
                    setRejectTarget(null);
                }}
                onOk={submitReject}
                destroyOnClose
            >
                <Typography.Paragraph type="secondary">
                    <FormattedMessage id="shareReview.rejectModal.hint" />
                </Typography.Paragraph>
                <Form form={rejectForm} layout="vertical">
                    <Form.Item
                        name="reason"
                        label={<FormattedMessage id="shareReview.rejectModal.reason" />}
                        rules={[
                            {
                                required: true,
                                message: formatMessage({ id: 'shareReview.rejectModal.reasonRequired' }),
                            },
                        ]}
                    >
                        <Input.TextArea rows={3} maxLength={50} showCount />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={metricsOpen}
                title={<FormattedMessage id="shareReview.metricsModal.title" />}
                okText={<FormattedMessage id="b.confirm" />}
                cancelText={<FormattedMessage id="b.cancel" />}
                confirmLoading={submitting}
                onCancel={() => {
                    setMetricsOpen(false);
                    setMetricsTarget(null);
                }}
                onOk={submitMetrics}
                destroyOnClose
            >
                <Form form={metricsForm} layout="vertical">
                    <Form.Item name="plays" label={<FormattedMessage id="shareReview.columns.plays" />}>
                        <InputNumber min={0} precision={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="likes" label={<FormattedMessage id="shareReview.columns.likes" />}>
                        <InputNumber min={0} precision={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="shares" label={<FormattedMessage id="shareReview.columns.shares" />}>
                        <InputNumber min={0} precision={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>
        </Permission>
    );
};

export default PageShareReview;
